<template>
	<div class="container-fluid mt-2">
		<PageHeader
			:data="{
				name: 'UTM &amp; Campaign Tracking',
				breadcrumbs: [{ name: 'Home', to: '/' }, { name: 'Marketing Tools', to: '/marketing/utm' }],
			}"
		/>

		<div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
			<div class="d-flex align-items-center">
				<label class="me-2 mb-0 text-muted small">Brand</label>
				<select class="form-select form-select-sm" style="width: 200px" v-model="selectedBrandId" :disabled="busy || !brands.length" @change="changeBrand">
					<option v-for="b in brands" :key="b.id" :value="b.id">{{ b.name }}</option>
				</select>
				<span v-if="refreshing" class="ms-2"><span class="spinner-border spinner-border-sm align-middle" role="status"></span> <span class="align-middle text-muted small">Updating</span></span>
			</div>
			<div class="btn-group btn-group-sm" role="group" aria-label="Date range">
				<button v-for="d in [7, 28, 90]" :key="d" type="button" class="btn" :class="d === rangeDays ? 'btn-primary' : 'btn-outline-secondary'" :disabled="busy || notConnected" @click="setRange(d)">{{ d }}d</button>
			</div>
		</div>

		<div v-if="errorMessage" class="alert alert-warning py-2 small">{{ errorMessage }}</div>

		<div v-if="loading" class="d-flex justify-content-center my-5">
			<div class="spinner-border" role="status"><span class="visually-hidden">Loading</span></div>
		</div>

		<div v-else-if="notConnected" class="card">
			<div class="card-body text-center py-5">
				<div class="fs-5 fw-bold mb-2">{{ selectedBrand ? selectedBrand.name : 'This brand' }} is not connected to GA4 yet</div>
				<p class="text-muted mb-3">Add this brand's GA4 property ID in Brands settings, then grant the service account access.</p>
				<router-link class="btn btn-primary btn-sm" to="/marketing/brands">Open Brands settings</router-link>
			</div>
		</div>

		<template v-else>
			<div :class="{ 'content-busy': refreshing }">
				<div class="text-muted small mb-3">
					GA4 property <span class="fw-bold">{{ propertyId || '-' }}</span>
					<span v-if="dateRange"> · {{ dateRange.start }} to {{ dateRange.end }}</span>
				</div>

				<!-- Coverage hero -->
				<div class="row g-3 mb-3">
					<div class="col-md-6">
						<div class="card h-100">
							<div class="card-body">
								<div class="text-muted small" title="Share of sessions that arrived with a utm_campaign tag">Campaign coverage</div>
								<div class="fs-1 fw-bold lh-1 my-1">{{ summary.tagged_pct || 0 }}%</div>
								<div class="progress my-2" style="height: 8px">
									<div class="progress-bar" role="progressbar" :style="{ width: (summary.tagged_pct || 0) + '%', backgroundColor: '#534AB7' }" :aria-valuenow="summary.tagged_pct || 0" aria-valuemin="0" aria-valuemax="100"></div>
								</div>
								<div class="small text-muted">{{ (summary.campaign_sessions || 0).toLocaleString() }} tagged, {{ (summary.untagged_sessions || 0).toLocaleString() }} untagged</div>
							</div>
						</div>
					</div>
					<div class="col-md-2 col-4">
						<div class="card h-100"><div class="card-body py-3 text-center">
							<div class="text-muted small" title="Distinct named UTM campaigns in range">Active campaigns</div>
							<div class="fs-4 fw-bold mt-1">{{ summary.distinct_campaigns || 0 }}</div>
						</div></div>
					</div>
					<div class="col-md-2 col-4">
						<div class="card h-100"><div class="card-body py-3 text-center">
							<div class="text-muted small" title="GA4 key events (configured conversions) from campaign-tagged sessions">Conversions</div>
							<div class="fs-4 fw-bold mt-1 text-success">{{ summary.campaign_key_events || 0 }}</div>
						</div></div>
					</div>
					<div class="col-md-2 col-4">
						<div class="card h-100"><div class="card-body py-3 text-center">
							<div class="text-muted small" title="Sessions from a named UTM campaign">Tagged sessions</div>
							<div class="fs-4 fw-bold mt-1">{{ (summary.campaign_sessions || 0).toLocaleString() }}</div>
						</div></div>
					</div>
				</div>

				<!-- Insight -->
				<div class="alert alert-info py-2 small" role="alert">{{ insight }}</div>

				<!-- Campaigns -->
				<div class="card mb-4">
					<div class="card-header d-flex justify-content-between align-items-center py-2">
						<span class="fw-bold">Campaigns <span class="text-muted fw-normal">(utm_campaign)</span></span>
						<input class="form-control form-control-sm" style="width: 200px" v-model="filterText" :disabled="busy" placeholder="Filter campaigns" aria-label="Filter campaigns" />
					</div>
					<table class="table table-sm table-hover align-middle mb-0" :class="{ 'pe-none': busy }">
						<thead>
							<tr>
								<th>Campaign</th>
								<th class="text-end">Sessions</th>
								<th class="text-end" title="GA4 key events from this campaign">Key events</th>
								<th class="text-end" title="Key events divided by sessions">Conv %</th>
								<th style="width: 28px"></th>
							</tr>
						</thead>
						<tbody>
							<template v-for="c in filteredCampaigns">
								<tr :key="c.campaign" class="cmp-row" :class="{ 'table-active': expanded === c.campaign }" @click="toggle(c.campaign)">
									<td>{{ c.campaign }}</td>
									<td class="text-end">{{ c.sessions.toLocaleString() }}</td>
									<td class="text-end">{{ c.key_events.toLocaleString() }}</td>
									<td class="text-end" :class="c.conv_rate > 0 ? 'text-success' : 'text-muted'">{{ c.conv_rate }}%</td>
									<td class="text-end text-muted">
										<span v-if="detailLoading && expanded === c.campaign" class="spinner-border spinner-border-sm"></span>
										<span v-else>{{ expanded === c.campaign ? '▴' : '▾' }}</span>
									</td>
								</tr>
								<tr v-if="expanded === c.campaign && !detailLoading" :key="c.campaign + '-detail'">
									<td colspan="5" class="bg-light">
										<div class="row">
											<div class="col-md-4 mb-2">
												<div class="small text-muted mb-1">Content (utm_content), e.g. email variants</div>
												<table class="table table-sm mb-0">
													<thead><tr><th>Content</th><th class="text-end">Sessions</th><th class="text-end">Key events</th></tr></thead>
													<tbody>
														<tr v-for="(row, i) in (detail ? detail.content : [])" :key="'dc' + i"><td>{{ row.label }}</td><td class="text-end">{{ row.sessions.toLocaleString() }}</td><td class="text-end">{{ (row.key_events || 0).toLocaleString() }}</td></tr>
														<tr v-if="detail && !detail.content.length"><td colspan="3" class="text-muted text-center">No utm_content recorded.</td></tr>
													</tbody>
												</table>
											</div>
											<div class="col-md-4 mb-2">
												<div class="small text-muted mb-1">Key events by event</div>
												<table class="table table-sm mb-0">
													<thead><tr><th>Event</th><th class="text-end">Count</th></tr></thead>
													<tbody>
														<tr v-for="(row, i) in (detail ? detail.key_events_by_name : [])" :key="'ke' + i"><td>{{ row.label }}</td><td class="text-end">{{ row.key_events.toLocaleString() }}</td></tr>
														<tr v-if="detail && !detail.key_events_by_name.length"><td colspan="2" class="text-muted text-center">No key events.</td></tr>
													</tbody>
												</table>
											</div>
											<div class="col-md-4 mb-2">
												<div class="small text-muted mb-1">Source / Medium</div>
												<table class="table table-sm mb-0">
													<thead><tr><th>Source / Medium</th><th class="text-end">Sessions</th></tr></thead>
													<tbody>
														<tr v-for="(row, i) in (detail ? detail.source_medium : [])" :key="'ds' + i"><td>{{ row.label }}</td><td class="text-end">{{ row.sessions.toLocaleString() }}</td></tr>
														<tr v-if="detail && !detail.source_medium.length"><td colspan="2" class="text-muted text-center">No data.</td></tr>
													</tbody>
												</table>
											</div>
										</div>
									</td>
								</tr>
							</template>
							<tr v-if="!filteredCampaigns.length"><td colspan="5" class="text-muted text-center py-3">No UTM-tagged campaigns in this range.</td></tr>
						</tbody>
					</table>
				</div>

				<!-- UTM breakdown -->
				<div class="d-flex justify-content-between align-items-center mb-2 flex-wrap">
					<div><span class="fw-bold">UTM breakdown</span> <span class="text-muted small">({{ scope === 'campaign' ? 'campaign traffic only' : 'all traffic' }})</span></div>
					<div class="btn-group btn-group-sm" role="group" aria-label="Breakdown scope">
						<button type="button" class="btn" :class="scope === 'campaign' ? 'btn-primary' : 'btn-outline-secondary'" :disabled="busy" @click="setScope('campaign')">Campaign traffic</button>
						<button type="button" class="btn" :class="scope === 'all' ? 'btn-primary' : 'btn-outline-secondary'" :disabled="busy" @click="setScope('all')">All traffic</button>
					</div>
				</div>
				<div class="row g-3 mb-4">
					<div class="col-md-3 col-sm-6">
						<div class="card h-100">
							<div class="card-header py-2 fw-bold" title="utm_source">Sources</div>
							<div class="card-body py-2">
								<div v-for="(r, i) in sources.slice(0, 6)" :key="'s' + i" class="d-flex justify-content-between small py-1"><span class="text-truncate me-2">{{ r.label }}</span><span>{{ r.sessions.toLocaleString() }}</span></div>
								<div v-if="!sources.length" class="text-muted small">No data</div>
							</div>
						</div>
					</div>
					<div class="col-md-3 col-sm-6">
						<div class="card h-100">
							<div class="card-header py-2 fw-bold" title="utm_medium">Mediums</div>
							<div class="card-body py-2">
								<div v-for="(r, i) in mediums.slice(0, 6)" :key="'m' + i" class="d-flex justify-content-between small py-1"><span class="text-truncate me-2">{{ r.label }}</span><span>{{ r.sessions.toLocaleString() }}</span></div>
								<div v-if="!mediums.length" class="text-muted small">No data</div>
							</div>
						</div>
					</div>
					<div class="col-md-3 col-sm-6">
						<div class="card h-100">
							<div class="card-header py-2 fw-bold d-flex justify-content-between" title="utm_content"><span>Content</span><span v-if="!content.dimension" class="text-warning small fw-normal">n/a</span></div>
							<div class="card-body py-2">
								<div v-for="(r, i) in content.rows.slice(0, 6)" :key="'c' + i" class="d-flex justify-content-between small py-1"><span class="text-truncate me-2">{{ r.label }}</span><span>{{ r.sessions.toLocaleString() }}</span></div>
								<div v-if="!content.rows.length" class="text-muted small">No utm_content</div>
							</div>
						</div>
					</div>
					<div class="col-md-3 col-sm-6">
						<div class="card h-100">
							<div class="card-header py-2 fw-bold d-flex justify-content-between" title="utm_term"><span>Terms</span><span v-if="!terms.dimension" class="text-warning small fw-normal">n/a</span></div>
							<div class="card-body py-2">
								<div v-for="(r, i) in terms.rows.slice(0, 6)" :key="'t' + i" class="d-flex justify-content-between small py-1"><span class="text-truncate me-2">{{ r.label }}</span><span>{{ r.sessions.toLocaleString() }}</span></div>
								<div v-if="!terms.rows.length" class="text-muted small">No utm_term</div>
							</div>
						</div>
					</div>
				</div>

				<!-- UTM link builder -->
				<div class="mb-5">
					<button class="btn btn-sm btn-outline-primary" :disabled="busy" @click="showBuilder = !showBuilder">{{ showBuilder ? 'Hide UTM link builder' : 'Build a UTM link' }}</button>
					<div v-if="showBuilder" class="card mt-2">
						<div class="card-body">
							<div class="row g-2">
								<div class="col-12">
									<label class="form-label mb-0 small text-muted">Destination URL</label>
									<input class="form-control form-control-sm" v-model="builder.baseUrl" placeholder="https://townweb.com/request-a-quote" />
								</div>
								<div class="col-md-4"><label class="form-label mb-0 small text-muted">utm_source</label><input class="form-control form-control-sm" v-model="builder.source" placeholder="postcard" /></div>
								<div class="col-md-4"><label class="form-label mb-0 small text-muted">utm_medium</label><input class="form-control form-control-sm" v-model="builder.medium" placeholder="directmail" /></div>
								<div class="col-md-4"><label class="form-label mb-0 small text-muted">utm_campaign</label><input class="form-control form-control-sm" v-model="builder.campaign" placeholder="townweb_referral_postcard_202606" /></div>
								<div class="col-md-6"><label class="form-label mb-0 small text-muted">utm_content</label><input class="form-control form-control-sm" v-model="builder.content" placeholder="email1" /></div>
								<div class="col-md-6"><label class="form-label mb-0 small text-muted">utm_term</label><input class="form-control form-control-sm" v-model="builder.term" placeholder="optional" /></div>
							</div>
							<div class="row g-2 mt-1">
								<div class="col-md-5"><label class="form-label mb-0 small text-muted">Label (optional)</label><input class="form-control form-control-sm" v-model="builderLabel" placeholder="June postcard run" /></div>
							</div>
							<div class="d-flex align-items-center mt-2">
								<input class="form-control form-control-sm me-2 bg-light" :value="builtUrl" readonly />
								<button class="btn btn-sm btn-outline-secondary text-nowrap me-2" :disabled="!builtUrl" @click="copyUrl">{{ copied ? 'Copied' : 'Copy' }}</button>
								<button class="btn btn-sm btn-primary text-nowrap" :disabled="!builtUrl || saving" @click="saveLink">Save</button>
							</div>
							<small class="text-muted">Values are normalised to lowercase with underscores so they stay consistent in GA4.</small>

							<div v-if="savedLinks.length" class="mt-3">
								<div class="small fw-bold mb-1">Saved links</div>
								<table class="table table-sm mb-0">
									<tbody>
										<tr v-for="link in savedLinks" :key="link.id">
											<td>
												<div class="small">{{ link.label || link.campaign || 'Link' }}</div>
												<div class="text-muted text-truncate" style="max-width: 520px; font-size: 12px">{{ link.full_url }}</div>
											</td>
											<td class="text-end text-nowrap">
												<button class="btn btn-sm btn-outline-secondary py-0 me-1" @click="copyText(link.full_url)">Copy</button>
												<button class="btn btn-sm btn-outline-danger py-0" @click="removeLink(link.id)">Remove</button>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
			</div>
		</template>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import restApi from '../../api'
import PageHeader from '@/components/layout/PageHeader.vue'

export default {
	name: 'UtmTracking',
	components: { PageHeader },
	data() {
		return {
			loading: false,
			refreshing: false,
			brands: [],
			selectedBrandId: null,
			rangeDays: 28,
			scope: 'campaign',
			propertyId: null,
			dateRange: null,
			summary: {},
			campaigns: [],
			sources: [],
			mediums: [],
			content: { dimension: null, rows: [] },
			terms: { dimension: null, rows: [] },
			filterText: '',
			expanded: null,
			detail: null,
			detailLoading: false,
			showBuilder: false,
			copied: false,
			errorMessage: '',
			builder: { baseUrl: '', source: '', medium: '', campaign: '', content: '', term: '' },
			builderLabel: '',
			savedLinks: [],
			saving: false,
		}
	},
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']),
		busy() {
			return this.loading || this.refreshing || this.detailLoading
		},
		selectedBrand() {
			return this.brands.find(b => b.id === this.selectedBrandId) || null
		},
		notConnected() {
			return this.selectedBrand && !this.selectedBrand.ga4_property_id
		},
		currentPropertyId() {
			return this.selectedBrand ? this.selectedBrand.ga4_property_id : null
		},
		insight() {
			const pct = this.summary.tagged_pct || 0
			const untagged = (this.summary.untagged_sessions || 0).toLocaleString()
			return `Only ${pct}% of sessions are campaign-tagged (${untagged} untagged). Standardising and tagging outbound (postcards, social, QR) is the biggest opportunity.`
		},
		filteredCampaigns() {
			const q = this.filterText.trim().toLowerCase()
			if (!q) return this.campaigns
			return this.campaigns.filter(c => c.campaign.toLowerCase().includes(q))
		},
		builtUrl() {
			if (!this.builder.baseUrl) return ''
			const params = []
			const add = (k, v) => {
				const n = this.norm(v)
				if (n) params.push(`${k}=${encodeURIComponent(n)}`)
			}
			add('utm_source', this.builder.source)
			add('utm_medium', this.builder.medium)
			add('utm_campaign', this.builder.campaign)
			add('utm_content', this.builder.content)
			add('utm_term', this.builder.term)
			if (!params.length) return this.builder.baseUrl
			let base = this.builder.baseUrl
			let fragment = ''
			const hashIdx = base.indexOf('#')
			if (hashIdx !== -1) {
				fragment = base.slice(hashIdx)
				base = base.slice(0, hashIdx)
			}
			const sep = base.includes('?') ? '&' : '?'
			return base + sep + params.join('&') + fragment
		},
	},
	created() {
		this.loadBrands()
	},
	methods: {
		startParam() {
			return `${this.rangeDays}daysAgo`
		},
		norm(v) {
			return (v || '').toString().trim().toLowerCase().replace(/\s+/g, '_')
		},
		loadBrands() {
			this.loading = true
			restApi
				.get('/marketing/brands')
				.then(({ data }) => {
					this.brands = Array.isArray(data) ? data : []
					this.afterBrands()
				})
				.catch(error => {
					this.brands = []
					this.loading = false
					this.errorMessage = 'Could not load brands. Check the API connection, or add a brand in Brands settings.'
					console.log(error)
				})
		},
		afterBrands() {
			if (!this.brands.length) {
				this.loading = false
				this.errorMessage = 'No brands configured yet. Add one in Brands settings.'
				return
			}
			const connected = this.brands.find(b => b.ga4_property_id)
			this.selectedBrandId = (connected || this.brands[0]).id
			if (this.notConnected) {
				this.loading = false
				return
			}
			this.loadMain(true)
		},
		changeBrand() {
			this.collapse()
			this.scope = 'campaign'
			if (this.notConnected) {
				this.loading = false
				this.refreshing = false
				return
			}
			this.loadMain(false)
		},
		setRange(d) {
			if (this.busy || d === this.rangeDays) return
			this.rangeDays = d
			this.collapse()
			this.loadMain(false)
		},
		setScope(s) {
			if (this.busy || s === this.scope) return
			this.scope = s
			this.loadMain(false)
		},
		collapse() {
			this.expanded = null
			this.detail = null
		},
		toggle(name) {
			if (this.busy) return
			if (this.expanded === name) {
				this.collapse()
				return
			}
			this.expanded = name
			this.detail = null
			this.detailLoading = true
			restApi
				.get('/marketing/utm/campaign', { params: { campaign: name, property_id: this.currentPropertyId, start: this.startParam(), end: 'today' } })
				.then(({ data }) => {
					this.detail = {
						content: data.content || [],
						source_medium: data.source_medium || [],
						key_events_by_name: data.key_events_by_name || [],
					}
					this.detailLoading = false
				})
				.catch(error => {
					this.detail = { content: [], source_medium: [], key_events_by_name: [] }
					this.detailLoading = false
					console.log(error)
				})
		},
		copyUrl() {
			if (!this.builtUrl) return
			navigator.clipboard.writeText(this.builtUrl).then(() => {
				this.copied = true
				setTimeout(() => (this.copied = false), 1500)
			})
		},
		copyText(text) {
			if (text) navigator.clipboard.writeText(text)
		},
		loadLinks() {
			restApi
				.get('/marketing/utm-links', { params: { brand_id: this.selectedBrand ? this.selectedBrand.id : null } })
				.then(({ data }) => {
					this.savedLinks = Array.isArray(data) ? data : []
				})
				.catch(() => {
					this.savedLinks = []
				})
		},
		saveLink() {
			if (!this.builtUrl) return
			this.saving = true
			restApi
				.post('/marketing/utm-links', {
					brand_id: this.selectedBrand ? this.selectedBrand.id : null,
					label: this.builderLabel || null,
					destination_url: this.builder.baseUrl,
					source: this.builder.source,
					medium: this.builder.medium,
					campaign: this.builder.campaign,
					content: this.builder.content,
					term: this.builder.term,
				})
				.then(({ data }) => {
					this.savedLinks.unshift(data)
					this.builderLabel = ''
					this.saving = false
				})
				.catch(error => {
					this.saving = false
					this.errorMessage = 'Could not save the UTM link.'
					console.log(error)
				})
		},
		removeLink(id) {
			restApi
				.delete(`/marketing/utm-links/${id}`)
				.then(() => {
					this.savedLinks = this.savedLinks.filter(l => l.id !== id)
				})
				.catch(() => {})
		},
		loadMain(initial) {
			if (initial) this.loading = true
			else this.refreshing = true
			this.errorMessage = ''
			restApi
				.get('/marketing/utm', { params: { property_id: this.currentPropertyId, start: this.startParam(), end: 'today', scope: this.scope } })
				.then(({ data }) => {
					this.propertyId = data.property_id
					this.dateRange = data.date_range || null
					this.summary = data.summary || {}
					this.campaigns = data.campaigns || []
					this.sources = data.sources || []
					this.mediums = data.mediums || []
					this.content = data.content || { dimension: null, rows: [] }
					this.terms = data.terms || { dimension: null, rows: [] }
					if (data.errors && data.errors.length) {
						this.errorMessage = 'Some sections could not be loaded: ' + data.errors.join('; ')
					}
					this.loadLinks()
					this.loading = false
					this.refreshing = false
				})
				.catch(error => {
					this.loading = false
					this.refreshing = false
					this.summary = {}
					this.campaigns = []
					this.sources = []
					this.mediums = []
					this.content = { dimension: null, rows: [] }
					this.terms = { dimension: null, rows: [] }
					this.collapse()
					this.errorMessage =
						(error.response && error.response.data && (error.response.data.message || error.response.data.error)) ||
						'Failed to load UTM analytics.'
					console.log(error)
				})
		},
	},
}
</script>

<style scoped>
.cmp-row {
	cursor: pointer;
}
.content-busy {
	opacity: 0.5;
	pointer-events: none;
	transition: opacity 0.15s ease;
}
</style>
